import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { ArrowsUpFromLine, FileBox, ListFilter, PackageCheck } from '@repo/media/icons';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { DataCard } from '@repo/components/data-card';
import { DateRangePicker } from '@repo/components/date-picker';
import { parseDateRange, serializeDateRange } from '@repo/components/date-picker/utils';
import type { DateRange } from '@repo/components/date-picker/utils';
import { LoadingIndicator } from '@repo/components/loading-indicator';
import { Menu, MenuItem, MenuSection, MenuTrigger } from '@repo/components/menu';
import { Pagination } from '@repo/components/pagination';
import { Popover } from '@repo/components/popover';
import { SearchField } from '@repo/components/search-field';

import { PageLoading } from '@repo/features/page-loading';
import { useNumberFormatter } from '@repo/utils/use-number-formatter';
import { kgToTonnes } from '@repo/utils/weight';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { PageError } from '~/features/page-error/page-error.tsx';
import { CargoesTable } from '~/routes/project.$project/(cargoes)/-components/cargoes-table.tsx';
import { SORT_OPTIONS, STATUS_OPTIONS } from '~/routes/project.$project/(cargoes)/-consts.ts';
import type { SortOption } from '~/routes/project.$project/(cargoes)/-consts.ts';
import { queryCargoes } from '~/services/cargo/cargo.ts';

const searchSchema = z.object({
  sortBy: z.enum(SORT_OPTIONS).catch('createdAt:desc'),
  status: z.enum(Object.values(STATUS_OPTIONS).map(({ key }) => key)).catch(STATUS_OPTIONS.all.key),
  search: z.string().catch(''),
  dateRange: z
    .object({
      start: z.iso.date(),
      end: z.iso.date(),
    })
    .optional()
    .catch(undefined),
  page: z.number().min(0).catch(0),
});
type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/project/$project/(cargoes)/cargoes/')({
  validateSearch: searchSchema.catch(() => searchSchema.parse({})),
  component: CargoesOverview,
  loader: async ({ context: { queryClient }, params: { project } }) => {
    await queryClient.ensureQueryData(queryCargoes(project));
  },
});

const PAGE_SIZE = 25;

function CargoesOverview() {
  const { project } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: cargoes,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useQuery(queryCargoes(project));
  const numberFormatter = useNumberFormatter();

  const [searchTerm, setSearchTerm] = useState(search.search);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const [dateRange, setDateRange] = useState<DateRange | null>(() => parseDateRange(search.dateRange));

  const [pageCount, setPageCount] = useState(0);
  const [filteredDataCount, setFilteredDataCount] = useState(cargoes?.length ?? 0);

  const totals = (cargoes ?? []).reduce((result, value) => {
    result.initial += value.receivedQuantityKilograms;
    result.emissions += value.totalEmissionsKilograms;
    result.stored += value.totalStoredKilograms;

    return result;
  }, { initial: 0, emissions: 0, stored: 0 });

  const updateSearchParams = useCallback(
    (params: Partial<SearchParams>) => {
      void navigate({ to: '.', search: prev => ({ ...prev, ...params }), replace: true, resetScroll: false });
    },
    [navigate],
  );

  const clearFilter = () => {
    updateSearchParams({ status: 'all', dateRange: undefined, page: 0, search: '' });
    setDateRange(null);
    setSearchTerm('');
  };

  const pagination = useMemo(() => ({
    pageIndex: search.page,
    pageSize: PAGE_SIZE,
  }), [search.page]);

  const columnFilters = useMemo(
    () => [
      {
        id: 'createdAt',
        value: {
          from: search.dateRange?.start ? new Date(search.dateRange.start) : undefined,
          to: search.dateRange?.end ? new Date(search.dateRange.end) : undefined,
        },
      },
      ...(search.status !== 'all'
        ? [{
            id: 'status',
            value: search.status,
          }]
        : []),
    ],
    [search.dateRange, search.status],
  );

  const sortingState = useMemo((): SortingState => {
    const [id = '', dir] = search.sortBy.split(':');
    return [{ id, desc: dir === 'desc' }];
  }, [search.sortBy]);

  useEffect(() => {
    const serializedDateRange = serializeDateRange(dateRange);
    const searchChanged = debouncedSearchTerm !== search.search;
    const dateChanged = search.dateRange?.start !== serializedDateRange?.start
      || search.dateRange?.end !== serializedDateRange?.end;

    if (searchChanged || dateChanged) {
      updateSearchParams({
        search: debouncedSearchTerm,
        dateRange: serializedDateRange,
        page: 0,
      });
    }
  }, [dateRange, debouncedSearchTerm, search.search, search.dateRange, updateSearchParams]);

  const renderPaginationInfo = () => {
    if (pageCount <= 1)
      return filteredDataCount;

    const start = search.page * PAGE_SIZE + 1;
    const end = Math.min(search.page * PAGE_SIZE + PAGE_SIZE, filteredDataCount);

    return `${start}-${end}`;
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError || !cargoes) {
    return (
      <PageError
        msg="An error occurred while loading cargoes."
        retry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <div className="@container flex flex-col items-start gap-6 pb-6">
      <div className="space-y-1 pb-4 w-full border-b border-b-secondary-200">
        <ContentHeader>Cargoes</ContentHeader>
        <p>
          Overview of cargoes in the site
        </p>
      </div>

      <div className={twMerge(
        'grid gap-4 w-full',
        '@3xl:grid-cols-3',
        'pb-6 border-b border-b-secondary-200',
      )}
      >
        <DataCard
          label="Total delivered"
          value={numberFormatter.format(kgToTonnes(totals.initial))}
          unit="tCO2"
          icon={FileBox}
          size="lg"
        />
        <DataCard
          label="Total emissions"
          value={numberFormatter.format(kgToTonnes(totals.emissions))}
          unit="tCO2"
          icon={ArrowsUpFromLine}
          size="lg"
        />
        <DataCard
          label="Total stored"
          value={numberFormatter.format(kgToTonnes(totals.stored))}
          unit="tCO2"
          icon={PackageCheck}
          size="lg"
        />
      </div>

      <div className="grid w-full sm:flex sm:flex-wrap items-start gap-4">
        <SearchField
          aria-label="Search cargoes"
          placeholder="Search cargoes"
          value={searchTerm}
          onChange={setSearchTerm}
        />

        <DateRangePicker
          aria-label="Filter cargoes by date range"
          value={dateRange}
          onChange={(dates) => { setDateRange(dates); }}
        />

        <MenuTrigger>
          <Button
            size="md"
            icon={{ component: <ListFilter size={16} strokeWidth={1.5} /> }}
            variant="soft"
          >
            {STATUS_OPTIONS[search.status].title}
          </Button>
          <Popover>
            <Menu>
              <MenuSection title="Sort by">
                {Object.values(STATUS_OPTIONS).map(option => (
                  <MenuItem key={option.key} onAction={() => { updateSearchParams({ status: option.key, page: 0 }); }}>
                    {option.title}
                  </MenuItem>
                ))}
              </MenuSection>
            </Menu>
          </Popover>
        </MenuTrigger>
      </div>

      <div className="w-full space-y-2">
        <Pagination
          currentPage={pagination.pageIndex}
          pageCount={pageCount}
          onChange={(page) => { updateSearchParams({ page }); }}
        />

        <Card className="max-w-full w-fit p-0">
          {pageCount > 1 && (
            <div className="p-4 pb-0">
              <div className="flex items-center gap-4">
                {isFetching && <LoadingIndicator size={16} className="text-primary-700" />}
                <p>
                  Showing
                  {' '}
                  {renderPaginationInfo()}
                  {' '}
                  of
                  {' '}
                  {filteredDataCount}
                  {' '}
                  certificates
                </p>
              </div>
            </div>
          )}
          <CargoesTable
            cargoes={cargoes}
            pagination={pagination}
            sorting={sortingState}
            setSorting={(sortBy: SortOption) => updateSearchParams({ sortBy })}
            columnFilters={columnFilters}
            setPageCount={setPageCount}
            setFilteredDataCount={setFilteredDataCount}
            globalFilter={search.search}
            clearFilter={clearFilter}
          />
        </Card>

        <Pagination
          currentPage={search.page}
          pageCount={pageCount}
          onChange={(page) => { updateSearchParams({ page }); }}
        />
      </div>
    </div>
  );
}
